import React, { useState, useEffect } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { ButtonVariantType } from "../../../Common/UI/Types";
import {
  Button,
  ButtonGroup,
  DialogHeader,
  Dialog,
  DialogInner,
  DialogContent,
  DialogFooter,
  Input
} from "../../../Common/UI/Index";
import { OutputConfigPickerItem } from "./Types";
import Summary from "~icons/ic/outline-article";
import Search from "~icons/ic/outline-search";
import "./Styles.scss";

interface Props {
  /**
   * Popup Title
   */
  title: string;
  /**
   * Action on close
   */
  onClose: () => void;
  /**
   * Output config list
   */
  configList: OutputConfigPickerItem[];
  onPick: (id: string, name: string) => void;
}

export const OutputConfigPickerView: React.FC<Props> = (props: Props) => {
  const { title, configList, onClose, onPick } = props;
  const { t: translate } = useTranslation();
  const [configId, setConfigId] = useState("");
  const [configName, setConfigName] = useState("");
  const [allowSelect, setAllowSelect] = useState(false);
  const [searchTerm, setsearchTerm] = useState("");
  const [filteredList, setFilteredList] = useState<OutputConfigPickerItem[]>([]);

  /**
   * On output config select
   * @param e SyntheticEvent
   * @param index selected index
   */
  const onSelect = (e: React.SyntheticEvent, index: number): void => {
    setConfigId(configList[index].outputConfigId);
    setConfigName(configList[index].name);
    setAllowSelect(true);
  };

  /**
   * Action when select output config button is selected
   */
  const onSelectOutputConfig = (): void => {
    onPick(configId, configName);
    onClose();
  };

  /**
   * Action on search value change
   * @param e SyntheticEvent
   * @param value string
   */
  const onSearchChange = (e: React.SyntheticEvent<HTMLInputElement>): void => {
    setsearchTerm(e.currentTarget.value);
  };

  /**
   * Clearing the search text
   */
  const onSearchClear = (): void => {
    setsearchTerm("");
  };

  /**
   * Update filtered list when search term changes
   */
  useEffect(() => {
    const term = (searchTerm && searchTerm.trim()) || "";
    if (term && term.length > 0) {
      const filtered = configList.filter((item: OutputConfigPickerItem) => {
        return item?.name?.toLowerCase().includes(searchTerm?.toLowerCase());
      });
      setFilteredList(filtered);
    }
    else if (term.length === 0) {
      setFilteredList(configList);
    }
  }, [searchTerm]);

  return (
    <div id ="ebt-output-picker-view">
      <Dialog
        open={true}
        onOpenChange={onClose}
        preventBackdropClose={true}
        className="ebt-output-picker-view"
      >
        <DialogInner className="ebt-output-picker-view-inner">
          <DialogHeader
            className="ebt-picker-header"
          >
            <div>
              {translate(title)}
            </div>
          </DialogHeader>
          <DialogContent className="ebt-config-picker">
            <p className="tree-subtitle">{translate("Scheduling.Heading.OutputConfigPickerSubtitle")}</p>
            <div className="output-config__searchBox">
              <Input
                dataTestId="ebt-btn-search"
                ariaLabel="with-icon"
                value={searchTerm}
                onChange={onSearchChange}
                clearable={true}
                onClear={onSearchClear}
              >
                <Search title="search" />
              </Input>
            </div>
            <div className="tree-holder">
              {
                filteredList.map((item: OutputConfigPickerItem, idx: number) =>
                  <Button
                    key={idx}
                    onClick={(e: React.SyntheticEvent): void => onSelect(e, idx)}
                    className={`tree-full-btn ${item.outputConfigId === configId ? "tree-full-btn-selected" : ""}`}
                  >
                    <Summary
                      color={ButtonVariantType.MONO}
                      title="Summary"
                    />
                    <span className="tree-btn-text">{item.name}</span>
                  </Button>
                )
              }
            </div>
          </DialogContent>
          <DialogFooter>
            <ButtonGroup>
              <Button
                borderless={true}
                onClick={onClose}
              >
                {translate("Button.Cancel")}
              </Button>
              <Button
                variant={ButtonVariantType.FILLED}
                onClick={onSelectOutputConfig}
                disabled={!allowSelect}
              >
                {translate("Button.SelectOutputConfig")}
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </DialogInner>
      </Dialog >
    </div>
  );
};
